import PrintSheet, { SheetField, SheetTitle } from "@/modules/print/sheet";
import { snapshotVatOf } from "@buildpy/core/modules/quote/snapshot-vat";
import { SnapshotData } from "@buildpy/core/modules/quote/types";
import { formatWon } from "@buildpy/core/lib/format";
import { formatDate } from "@/modules/quote/ui/format";
import Money from "@/components/money";

interface Props {
  data: SnapshotData;
  /** 현재 조직 정보. 주면 스냅샷의 회사 정보 대신 이걸 쓴다(상호·연락처가 바뀐 경우). */
  org?: { name: string; tel: string; address: string };
}

/**
 * 갑지. 고객이 제일 먼저 보는 한 장이라 금액 하나와 조건만 크게 둔다.
 * 공종·항목은 뒤 장의 일이다.
 */
const QuoteCover = ({ data, org }: Props) => {
  const company = org ?? data.org;
  const { quote, customer } = data;
  const vat = snapshotVatOf(data);

  return (
    <PrintSheet>
      <SheetTitle>견 적 서</SheetTitle>

      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs text-[#525252]">발주처</p>
          <p className="text-lg font-semibold">{customer.name} 귀하</p>
          {customer.site_address && (
            <p className="text-xs text-[#525252]">{customer.site_address}</p>
          )}
        </div>

        <div className="min-w-0 text-right text-xs leading-5">
          <p className="text-sm font-semibold">{company.name}</p>
          {company.address && <p>{company.address}</p>}
          {company.tel && <p>TEL. {company.tel}</p>}
        </div>
      </div>

      <div className="mb-4 border-t border-[#d9d9d9]">
        <SheetField label="견적번호">
          {quote.quote_no} (v{quote.version})
        </SheetField>
        <SheetField label="견적일자">{formatDate(quote.sent_at)}</SheetField>
        <SheetField label="공사명">{quote.title}</SheetField>
        <SheetField label="도급금액">
          <Money className="text-base font-bold" value={vat.total} />
          <span className="ml-2 text-[#525252]">(부가세 포함)</span>
          {/* 별도는 「공급가 + 부가세」 로 · 포함은 그 안의 공급가·부가세 */}
          <span className="block text-[#525252] tabular-nums" data-testid="quote-cover-vat">
            {vat.mode === "EXCL"
              ? `공급가액 ${formatWon(vat.supply)} + 부가세 ${formatWon(vat.vat)}`
              : `그중 공급가액 ${formatWon(vat.supply)} · 부가세 ${formatWon(vat.vat)}`}
          </span>
        </SheetField>
      </div>

      <div className="mb-4">
        <p className="mb-1 text-xs font-semibold">견적조건</p>
        {quote.conditions.length === 0 ? (
          <p className="text-xs text-[#737373]">등록된 조건이 없습니다.</p>
        ) : (
          <ol className="list-decimal space-y-0.5 pl-4 text-xs leading-5">
            {quote.conditions.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ol>
        )}
      </div>

      {quote.extra_notice && (
        <div className="border border-[#d9d9d9] bg-[#fafafa] p-2.5">
          <p className="mb-1 text-xs font-semibold">별도 부담금 안내</p>
          <p className="whitespace-pre-wrap text-xs leading-5">
            {quote.extra_notice}
          </p>
        </div>
      )}
    </PrintSheet>
  );
};

export default QuoteCover;
